import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authMe, authLogin, authRegister, authLogout, authGoogleSession, importLibraryApi } from "@/lib/api";
import { getLibrary, clearLibrary } from "@/lib/library";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const u = await authMe();
      setUser(u);
      return u;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return; // AuthCallback handles it
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const migrateLocal = async () => {
    const local = getLibrary();
    if (local.length) {
      try {
        await importLibraryApi(local);
        clearLibrary();
      } catch (e) {
        console.warn("Could not move saved prompts to your account", e);
      }
    }
  };

  const login = async (email, password) => {
    const u = await authLogin(email, password);
    setUser(u);
    await migrateLocal();
    return u;
  };

  const register = async (email, password, name) => {
    const u = await authRegister(email, password, name);
    localStorage.removeItem("stp_ref");
    setUser(u);
    await migrateLocal();
    return u;
  };

  const completeGoogle = async (sessionId) => {
    const u = await authGoogleSession(sessionId);
    localStorage.removeItem("stp_ref");
    setUser(u);
    await migrateLocal();
    return u;
  };

  const logout = async () => {
    try {
      await authLogout();
    } catch (e) {
      console.warn("Logout request failed", e);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, completeGoogle, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
